import {
    CampaignUrl,
    EditCampaignUrl,
    getSearchLocationRequest,
    isLocationChangeAction,
    getAdUnitAndFormatInfoRequest,
    getKPIRequest
} from "@common";
import { AppState, openNotificationAction } from "@store";
import { FetchingState } from "rd-redux-http";
import { LocationChangeAction, LOCATION_CHANGE } from "react-router-redux";
import { Action } from "redux";
import { all, put, select, takeEvery } from "redux-saga/effects";
import { debounce } from "../../saga-effects";
import {
    changeCampaignChangesFlagAction,
    changeUpdateNavStateFlagAction,
    createOrUpdateCampaignAction,
    deleteCampaignEditingDataAction,
    getCampaignByIdAction,
    openTimetableWarningAction,
    purchaseCampaignByIdAction,
    searchLocationByQueryAction,
    setAdUnitTypeAction,
    setPriceModelAction,
    setAdUnitFormatAction,
    setPrimaryKPIAction
} from "../actions";
import { campaignBudgetForm, campaignCreativesForm, campaignMediaPlanForm } from "../state";
import { createOrUpdateCampaign } from "./createOrUpdateCampaign";
import { loadCampaignById } from "./loadCampaignById";
import { loadDataForCampaignPageWithRequiredFields } from "./loadDataForCampaignPageWithRequiredFields";
import { purchaseCampaign } from "./purchaseCampaign";
import { recalculateEstimatedImpression } from "./recalculateEstimatedImpression";
import { KPIMapping } from "../../api";

function* searchByLocationQuery(action: typeof searchLocationByQueryAction.typeInterface) {
    const appState: AppState = yield select();
    const { query, region } = action;
    const placeAndZipStore = appState.campaignState.placeAndZipList;

    if (FetchingState.isSuccess(placeAndZipStore)) {
        if (
            placeAndZipStore.params["search-place"] === query["search-place"] &&
            placeAndZipStore.params["search-zip"] === query["search-zip"] &&
            placeAndZipStore.params.region === query["region"]
        )
            return;
    }

    try {
        yield* getSearchLocationRequest.saga({ ...{ region }, ...query });
    } catch (e) {
        console.log("Fetch location by query error", e.message);
    }
}

export function* campaignSaga() {
    yield all([
        takeEvery(
            (a: Action) =>
                (a.type === LOCATION_CHANGE &&
                    CampaignUrl.match((a as LocationChangeAction).payload, true).isMatched) ||
                EditCampaignUrl.match((a as LocationChangeAction).payload, true).isMatched,
            loadDataForCampaignPageWithRequiredFields
        ),
        takeEvery(
            (a: Action) =>
                a.type === LOCATION_CHANGE && CampaignUrl.match((a as LocationChangeAction).payload, true).isMatched,
            deleteEditCampaignFromStore
        ),
        takeEvery(createOrUpdateCampaignAction.type, createOrUpdateCampaign),
        takeEvery(purchaseCampaignByIdAction.type, purchaseCampaign),
        takeEvery(getCampaignByIdAction.type, loadCampaignById),
        takeEvery(isEditCampaignPageNavigated, loadDataForCampaignPageWithRequiredFields),
        takeEvery(
            (a: Action) =>
                (campaignMediaPlanForm.actions.isFieldEdit(a) && a.field !== "campaignName") ||
                (campaignBudgetForm.actions.isFieldEdit(a) && a.field !== "budget"),
            recalculateEstimatedImpression
        ),
        debounce(
            100,
            (a: Action) => campaignBudgetForm.actions.isFieldEdit(a) && a.field === "budget",
            recalculateEstimatedImpression
        ),
        takeEvery(searchLocationByQueryAction.type, searchByLocationQuery),
        takeEvery(setAdUnitFormatAction.type, setAdUnitFormat),
        takeEvery(
            (a: Action) =>
                a.type === campaignMediaPlanForm.actions.types.FIELD_EDIT ||
                a.type === campaignCreativesForm.actions.types.FIELD_EDIT ||
                a.type === campaignBudgetForm.actions.types.FIELD_EDIT,
            leaveCampaignPage
        ),
        takeEvery(
            (a: Action) =>
                a.type === LOCATION_CHANGE &&
                CampaignUrl.match((a as LocationChangeAction).payload, true).isMatched &&
                EditCampaignUrl.match((a as LocationChangeAction).payload, true).isMatched,
            resetCampaignNavState
        ),
        takeEvery(
            (a: Action) =>
                a.type === LOCATION_CHANGE &&
                !CampaignUrl.match((a as LocationChangeAction).payload, true).isMatched &&
                !EditCampaignUrl.match((a as LocationChangeAction).payload, true).isMatched,
            resetCampaignChangesFlag
        ),
        takeEvery(setAdUnitTypeAction.type, setAdUnitType),
        takeEvery(setPriceModelAction.type, setPriceModel),
        takeEvery(setPrimaryKPIAction.type, setPrimaryModel),
        takeEvery(openTimetableWarningAction.type, openTimetableWarning),
        takeEvery((a: Action) => campaignBudgetForm.actions.isFieldEndEditing(a), function*(a: Action) {
            if (campaignBudgetForm.actions.isFieldEndEditing(a)) {
                const state: AppState = yield select();
                const formData = campaignBudgetForm.selector(state.campaignState.campaignBudgetForm);

                if (formData.fields.budget.isParsed) {
                    if (formData.fields.budget.data <= 0) {
                        yield put(
                            campaignBudgetForm.actions.setErrors({
                                fields: {
                                    budget: ["Budget should be greater than zero."]
                                }
                            })
                        );
                    }

                    if (formData.fields.budget.data >= 999999999) {
                        yield put(
                            campaignBudgetForm.actions.setErrors({
                                fields: {
                                    budget: ["Budget is too high."]
                                }
                            })
                        );
                    }
                }
            }
        })
    ]);
}

function isEditCampaignPageNavigated(action: Action) {
    return isLocationChangeAction(action) && EditCampaignUrl.match(action.payload, true).isMatched;
}

function* leaveCampaignPage(action: Action) {
    const appState: AppState = yield select();
    if (appState.campaignState.campaignChangesFlag) return;
    yield put(changeCampaignChangesFlagAction({ status: true }));
}

function* resetCampaignChangesFlag(action: Action) {
    const appState: AppState = yield select();
    if (!appState.campaignState.campaignChangesFlag) return;
    yield put(changeCampaignChangesFlagAction({ status: false }));
}

function* resetCampaignNavState() {
    const appState: AppState = yield select();
    if (appState.campaignState.updateNavStateFlag) return;
    yield put(changeUpdateNavStateFlagAction({ status: true }));
}

function* deleteEditCampaignFromStore(action: Action) {
    yield put(deleteCampaignEditingDataAction());
    yield put(campaignMediaPlanForm.actions.setData({}));
    yield put(campaignCreativesForm.actions.setData({}));
    yield put(campaignBudgetForm.actions.setData({}));
    yield put(campaignMediaPlanForm.actions.reset());
    yield put(campaignCreativesForm.actions.reset());
    yield put(campaignBudgetForm.actions.reset());
}

function* setAdUnitType(action: typeof setAdUnitTypeAction.typeInterface) {
    yield put(campaignCreativesForm.actions.fieldEdit("adUnitType", action.adUnitType));
}

function* setPriceModel(action: typeof setPriceModelAction.typeInterface) {
    const state: AppState = yield select();
    const { priceModel } = action;

    if (!FetchingState.isSuccess(state.mappings.kpi)) {
        yield* getKPIRequest.saga({});
    }

    const kpiMapping: any = FetchingState.isSuccess(state.mappings.kpi) && state.mappings.kpi.data;

    yield put(campaignMediaPlanForm.actions.fieldEdit("priceModel", priceModel ? priceModel : undefined));

    const primary = kpiMapping && kpiMapping.filter((kpi: KPIMapping) => kpi.unit === priceModel)[0].name;

    yield put(campaignMediaPlanForm.actions.fieldEdit("primaryKPI", primary ? primary : undefined));

    yield put(campaignMediaPlanForm.actions.fieldEdit("secondaryKPI", undefined));

    yield put(campaignMediaPlanForm.actions.resetFieldState(["secondaryKPI"]));
}

function* setPrimaryModel(action: typeof setPrimaryKPIAction.typeInterface) {
    const state: AppState = yield select();
    const { primaryKPI } = action;

    if (!FetchingState.isSuccess(state.mappings.kpi)) {
        yield* getKPIRequest.saga({});
    }

    const kpiMapping: any = FetchingState.isSuccess(state.mappings.kpi) && state.mappings.kpi.data;

    const selectedKPIMapping = kpiMapping && kpiMapping.find((k: KPIMapping) => k.name === primaryKPI);

    const priceModel = selectedKPIMapping.unit;

    yield put(campaignMediaPlanForm.actions.fieldEdit("priceModel", priceModel ? priceModel : undefined));

    yield put(campaignMediaPlanForm.actions.fieldEdit("primaryKPI", primaryKPI ? primaryKPI : undefined));

    yield put(campaignMediaPlanForm.actions.fieldEdit("secondaryKPI", undefined));

    yield put(campaignMediaPlanForm.actions.resetFieldState(["secondaryKPI"]));
}

function* openTimetableWarning(action: typeof openTimetableWarningAction.typeInterface) {
    yield put(
        openNotificationAction({
            isOpen: true,
            isWarning: true,
            message: "At least one time slot needs to be selected"
        })
    );
}

function* setAdUnitFormat(action: typeof setAdUnitFormatAction.typeInterface) {
    yield put(campaignMediaPlanForm.actions.fieldEdit("adUnitFormat", action.adUnitFormat));

    const appState: AppState = yield select();
    const adunits = appState.mappings.adunits;
    if (!FetchingState.isSuccess(adunits)) {
        yield* getAdUnitAndFormatInfoRequest.saga({});
    }

    let adUnitTypeName: string = undefined;

    if (FetchingState.isSuccess(adunits)) {
        const firstAdunit = adunits.data[0].types.find(t => t.name === "Static Banner");
        adUnitTypeName = firstAdunit ? firstAdunit.name : undefined;
    }

    yield put(campaignCreativesForm.actions.fieldEdit("adUnitType", adUnitTypeName));
    yield put(campaignCreativesForm.actions.fieldEdit("adUnitId", undefined));
}
